<template>
  <v-dialog v-model="dialog" max-width="620" persistent>
    <v-card class="trial">
      <v-card-title class="trial__title">{{
        title || $t("trialDialog.title")
      }}</v-card-title>

      <v-card-text>
        <p class="trial__lead">
          {{ $t("trialDialog.lead") }}
        </p>

        <!-- Passo 1: o dia. Só entram datas em que o professor tem horário
             livre, para o aluno não escolher um dia sem opção. -->
        <div class="trial__step">
          <div class="trial__step-label">{{ $t("trialDialog.day") }}</div>

          <div v-if="!days.length" class="trial__empty">
            {{ $t("trialDialog.noSlots") }}
          </div>

          <div v-else class="trial__days">
            <button
              v-for="day in days"
              :key="day.date"
              type="button"
              class="trial__day"
              :class="{ 'trial__day--on': selectedDate === day.date }"
              @click="pickDay(day)"
            >
              <span class="trial__day-week">{{ day.weekLabel }}</span>
              <span class="trial__day-num">{{ day.dayLabel }}</span>
              <span class="trial__day-month">{{ day.monthLabel }}</span>
            </button>
          </div>
        </div>

        <!-- Passo 2: o horário daquele dia. -->
        <div v-if="selectedDate" class="trial__step">
          <div class="trial__step-label">{{ $t("trialDialog.time") }}</div>
          <div class="trial__hours">
            <button
              v-for="hour in hoursOfSelectedDay"
              :key="`${hour.from}-${hour.to}`"
              type="button"
              class="trial__hour"
              :class="{ 'trial__hour--on': isSelectedHour(hour) }"
              @click="selectedHour = hour"
            >
              {{ $t("trialDialog.range", { from: hour.from, to: hour.to }) }}
            </button>
          </div>
        </div>

        <p v-if="selectedHour" class="trial__summary">
          <i18n path="trialDialog.summary" tag="span">
            <template #when>
              <b>{{ summaryLabel }}</b>
            </template>
          </i18n>
        </p>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="busy" @click="close">{{
          $t("common.cancel")
        }}</v-btn>
        <v-btn
          color="#E8276F"
          dark
          depressed
          :loading="busy"
          :disabled="!selectedHour || busy"
          @click="confirm"
        >
          {{ confirmLabel || $t("trialDialog.title") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { languageTag } from "@/plugins/i18n";

// Card 86aje36nz: "deve aparecer o formato de calendário, onde o aluno escolhe
// o dia e então o horário, semelhante ao caminho de remarcação de aula".
//
// A lista de horários livres do professor vem por dia da semana; aqui ela é
// projetada nas próximas semanas para virar datas concretas — o aluno pensa em
// "quinta que vem", não em "quinta-feira" como categoria.
const WEEKS_AHEAD = 4;

export default {
  name: "TrialScheduleDialog",

  props: {
    title: { type: String, default: "" },
    // O mesmo calendário serve ao pedido do aluno e à sugestão do professor.
    confirmLabel: { type: String, default: "" },
    // [{ weekDay, from, to }] — disponibilidade livre do professor.
    availability: { type: Array, default: () => [] },
    // [{ date: "YYYY-MM-DD", from, to }] — aulas já marcadas nessas datas.
    // O horário que colide com uma delas some daquele dia (card 86aje36nz:
    // o professor não pode sugerir por cima da própria aula).
    blocked: { type: Array, default: () => [] },
    busy: { type: Boolean, default: false },
  },

  data() {
    return {
      dialog: false,
      selectedDate: null,
      selectedHour: null,
    };
  },

  computed: {
    // Datas concretas das próximas semanas que têm ao menos um horário livre.
    days() {
      const byWeekDay = new Map();

      (this.availability || []).forEach((slot) => {
        const key = Number(slot.weekDay);
        if (!byWeekDay.has(key)) byWeekDay.set(key, []);
        byWeekDay.get(key).push(slot);
      });

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const result = [];

      for (let offset = 1; offset <= WEEKS_AHEAD * 7; offset += 1) {
        const date = new Date(today);
        date.setDate(date.getDate() + offset);

        const iso = this.toISODate(date);
        const slots = (byWeekDay.get(date.getDay()) || []).filter(
          (slot) => !this.isBlocked(iso, slot)
        );
        if (!slots.length) continue;

        result.push({
          date: iso,
          weekDay: date.getDay(),
          weekLabel: date
            .toLocaleDateString(languageTag(), { weekday: "short" })
            .replace(".", ""),
          dayLabel: String(date.getDate()).padStart(2, "0"),
          monthLabel: date.toLocaleDateString(languageTag(), {
            month: "short",
          }),
          hours: [...slots].sort((left, right) =>
            left.from.localeCompare(right.from)
          ),
        });
      }

      return result;
    },
    hoursOfSelectedDay() {
      const day = this.days.find((item) => item.date === this.selectedDate);
      return day ? day.hours : [];
    },
    summaryLabel() {
      const day = this.days.find((item) => item.date === this.selectedDate);

      if (!day || !this.selectedHour) return "";

      return this.$t("trialDialog.summaryWhen", {
        weekday: day.weekLabel,
        day: day.dayLabel,
        month: day.monthLabel,
        time: this.selectedHour.from,
      });
    },
  },

  methods: {
    open() {
      this.selectedDate = null;
      this.selectedHour = null;
      this.dialog = true;
    },
    close() {
      this.dialog = false;
    },
    toISODate(date) {
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${date.getFullYear()}-${month}-${day}`;
    },
    // Sobreposição de intervalos "HH:MM" no mesmo dia.
    isBlocked(date, slot) {
      return (this.blocked || []).some(
        (item) =>
          item.date === date && item.from < slot.to && item.to > slot.from
      );
    },
    pickDay(day) {
      this.selectedDate = day.date;
      // Trocar de dia sem limpar deixaria um horário de outro dia selecionado.
      this.selectedHour = null;
    },
    isSelectedHour(hour) {
      return (
        this.selectedHour &&
        this.selectedHour.from === hour.from &&
        this.selectedHour.to === hour.to
      );
    },
    confirm() {
      if (!this.selectedHour) return;

      // A experimental é uma aula só: vai um horário, com a data escolhida
      // junto — o professor confirma exatamente esse encontro.
      this.$emit("confirmed", [
        {
          weekDay: this.selectedHour.weekDay,
          from: this.selectedHour.from,
          to: this.selectedHour.to,
          date: this.selectedDate,
        },
      ]);
    },
  },
};
</script>

<style scoped>
.trial__title {
  font-size: 1.1rem;
  font-weight: 700;
}

.trial__lead {
  font-size: 0.9rem;
  color: var(--dk-muted, #6b6472);
}

.trial__step {
  margin-top: 18px;
}

.trial__step-label {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--dk-muted, #6b6472);
  margin-bottom: 10px;
}

.trial__days {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 6px;
}

.trial__day {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 64px;
  padding: 8px 6px;
  border: 1px solid var(--dk-line, #e9e4f0);
  border-radius: 12px;
  background: var(--dk-surface, #fff);
  cursor: pointer;
}

.trial__day--on {
  border-color: var(--dk-pink, #e8276f);
  background: rgba(232, 39, 111, 0.07);
}

.trial__day-week {
  font-size: 0.7rem;
  text-transform: uppercase;
  color: var(--dk-muted, #6b6472);
}

.trial__day-num {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--dk-ink, #231a2e);
  line-height: 1.2;
}

.trial__day-month {
  font-size: 0.7rem;
  color: var(--dk-muted, #6b6472);
}

.trial__hours {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.trial__hour {
  border: 1px solid var(--dk-line, #e9e4f0);
  border-radius: 999px;
  background: var(--dk-surface, #fff);
  padding: 7px 14px;
  font-size: 0.85rem;
  color: var(--dk-ink-soft, #4b4551);
  cursor: pointer;
}

.trial__hour--on {
  border-color: var(--dk-pink, #e8276f);
  background: rgba(232, 39, 111, 0.07);
  color: var(--dk-pink, #e8276f);
  font-weight: 600;
}

.trial__empty {
  font-size: 0.88rem;
  color: var(--dk-faint, #9b93a3);
}

.trial__summary {
  margin-top: 18px;
  margin-bottom: 0;
  font-size: 0.9rem;
  color: var(--dk-ink, #231a2e);
}
</style>
